/**
 * Ce qu'on peut mettre à la place d'une ligne de contre-étude.
 *
 * *Signalé par Chris le 27/08/2026, sur AQUALIA : « sur la ligne 66 sable de remblai, je veux
 * modifier et mettre un autre matériau, et la liste de la grille tarifaire n'apparaît pas pour
 * en sélectionner un autre ».*
 *
 * La liste déroulante ne proposait que les 102 ressources génériques, main-d'œuvre et matériel.
 * Un matériau ne pouvait donc être remplacé que par une machine ou un homme — c'est-à-dire par
 * rien de ce qu'on cherche quand on change un sable pour un gravier. Les 37 matériaux de la
 * grille tarifaire des carrières s'ajoutent après le matériel, dans le même ordre que le menu
 * d'ajout (`AjoutRessource`), pour que les deux gestes montrent le même référentiel.
 *
 * **Le catalogue d'achat n'y est pas, et c'est délibéré** : ses ~3 000 articles ne tiennent pas
 * dans une liste déroulante — c'est la raison pour laquelle l'ajout les cherche par frappe, dans
 * un champ à part. Le serveur, lui, accepte leur code au remplacement comme à l'ajout : le jour
 * où l'écran saura les chercher ici, rien ne sera à changer derrière.
 *
 * Écrit hors du composant parce que c'est la seule partie qui se vérifie sans navigateur : que
 * le code porté par la ligne reste choisi même quand aucun référentiel ne le connaît, et qu'il
 * n'apparaisse jamais deux fois.
 */

/** Ce que la liste a besoin de savoir d'une ressource générique. */
export type ChoixGenerique = {
  readonly id: string;
  readonly code: string;
  readonly libelle: string;
  readonly type: string;
};

/** Ce qu'elle a besoin de savoir d'un matériau de la grille tarifaire. */
export type ChoixMateriau = {
  readonly id: string;
  readonly code: string;
  readonly libelle: string;
};

export type OptionRemplacement = {
  readonly cle: string;
  readonly code: string;
  readonly libelle: string;
};

export type GroupeRemplacement = {
  readonly titre: string;
  readonly options: readonly OptionRemplacement[];
};

/**
 * Les groupes de la liste, dans l'ordre où ils s'affichent.
 *
 * Un groupe vide n'est pas rendu : une grille tarifaire non chargée — le client qui n'en a pas
 * encore, ou une lecture en panne — laisserait sinon un intitulé sans rien dessous, qui se lit
 * comme une liste cassée.
 */
export function groupesDeRemplacement(
  generiques: readonly ChoixGenerique[],
  materiaux: readonly ChoixMateriau[],
): readonly GroupeRemplacement[] {
  const groupes: GroupeRemplacement[] = [
    {
      titre: "Main-d'œuvre",
      options: generiques.filter((g) => g.type === "MO").map(optionGenerique),
    },
    {
      titre: "Matériel",
      options: generiques.filter((g) => g.type === "MAT").map(optionGenerique),
    },
    {
      titre: "Grille tarifaire (carrières)",
      // Le code d'un matériau de la grille peut être celui d'un générique — rien ne
      // l'interdit dans deux référentiels tenus séparément. La clé de rendu porte donc la
      // provenance : deux options de même code dans deux groupes différents restent deux
      // lignes distinctes de la liste, et React ne les confond pas.
      options: materiaux.map((m) => ({
        cle: `tarif:${m.id}`,
        code: m.code,
        libelle: m.libelle,
      })),
    },
  ];
  return groupes.filter((g) => g.options.length > 0);
}

/**
 * Le code de la ligne est-il proposé quelque part ?
 *
 * Sinon la liste doit l'afficher elle-même, en tête et marqué « hors référentiel » : un
 * `<select>` dont la valeur ne figure dans aucune option n'affiche **rien**, et la cellule du
 * code paraîtrait vide sur une ligne qui en porte pourtant un. C'est le cas de tous les codes
 * du catalogue d'achat, et de tout code d'un classeur qu'aucun référentiel ne connaît.
 */
export function estProposee(
  code: string,
  groupes: readonly GroupeRemplacement[],
): boolean {
  return groupes.some((g) => g.options.some((o) => o.code === code));
}

function optionGenerique(g: ChoixGenerique): OptionRemplacement {
  return { cle: `generique:${g.id}`, code: g.code, libelle: g.libelle };
}
